import { dehydrate, HydrationBoundary } from '@tanstack/react-query';
import { Suspense } from 'react';

import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getQueryClient } from '../../../../../../getQueryClient';
import { resolveStoryblokStyles, sbEditable, type SBProps } from '../../../../../utils';
import { CategoriesBarSkeleton } from './CategoriesBarSkeleton';
import { DesktopCategories } from './DesktopCategories';
import { getCategoryBarItems, getCategoryBarQueryKey } from './getCategoryBarItems';
import { MobileCategories } from './MobileCategories';
import type { SBCategoriesBarProps } from './types';

export const SBCategoriesBar = ({ blok }: SBProps<SBCategoriesBarProps>) => {
  const { mtMobile, mtTablet, mtDesktop, mbMobile, mbTablet, mbDesktop, categories } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const categoryItems = categories
    ?.filter(category => typeof category === 'string' || category.uuid)
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }

      return item.uuid;
    }) as string[];

  if (!isArrayWithLength(categoryItems)) {
    return null;
  }

  const queryClient = getQueryClient();

  queryClient.prefetchQuery({
    queryKey: getCategoryBarQueryKey({ categoryItems, draftMode: false }),
    queryFn: async () => getCategoryBarItems({ categoryItems, draftMode: false }),
  });

  return (
    <div
      className={cn(className, 'mx-auto max-w-screen-xl px-4 text-gray-500 md:px-6 lg:px-8')}
      {...sbEditable(blok)}
    >
      <HydrationBoundary state={dehydrate(queryClient)}>
        <Suspense fallback={<CategoriesBarSkeleton />}>
          <MobileCategories categoryItems={categoryItems} />
          <DesktopCategories categoryItems={categoryItems} />
        </Suspense>
      </HydrationBoundary>
    </div>
  );
};
